/**
 * JOBMATCH NG — ADMIN TELEGRAM OPERATIONS & DESKS SECTION
 * 
 * Comprehensive administrative operations dashboard for monitoring,
 * diagnosing, testing, and managing the Telegram AI Assistant channel.
 */

import React, { useState, useEffect } from 'react';
import {
  Bot,
  Webhook,
  Server,
  Sparkles,
  Database,
  Send,
  MessageSquare,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  ShieldCheck,
  Terminal,
  Activity,
  Trash2,
  Check,
  Copy,
  ExternalLink,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { getAdminHeaders } from '../../lib/supabaseService';

interface TelegramOverviewData {
  config: {
    configured: boolean;
    botUsername?: string;
    botName?: string;
    botId?: number;
    webhookUrl?: string;
  };
  health: {
    status?: 'ONLINE' | 'OFFLINE' | 'ERROR' | 'NOT CONFIGURED';
    checks?: Array<{ component: string; status: 'PASS' | 'FAIL' | 'WARNING'; message: string }>;
    report?: Array<{ component: string; status: 'PASS' | 'FAIL' | 'WARNING'; message: string }>;
    overall?: 'READY' | 'NOT READY';
    details?: any;
  };
  webhookInfo: {
    configured: boolean;
    info?: {
      url: string;
      hasCustomCertificate: boolean;
      pendingUpdateCount: number;
      lastErrorDate?: number;
      lastErrorMessage?: string;
      maxConnections?: number;
      ipAddress?: string;
    };
    error?: string;
  };
  inboundLogs: any[];
  outboundLogs: any[];
  aiLogs: any[];
  errorCenter: any[];
  conversations: any[];
  liveEvents: any[];
}

export const AdminTelegramSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<TelegramOverviewData | null>(null);

  // Diagnostic state
  const [diagnosticRunning, setDiagnosticRunning] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState<any | null>(null);
  const [diagnosticError, setDiagnosticError] = useState<string | null>(null);

  // Test message state
  const [testChatId, setTestChatId] = useState('');
  const [testMessageText, setTestMessageText] = useState('JobMatch NG Telegram test message successful.');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState<any | null>(null);

  // Webhook action state
  const [webhookUrlInput, setWebhookUrlInput] = useState('');
  const [registeringWebhook, setRegisteringWebhook] = useState(false);
  const [webhookActionMsg, setWebhookActionMsg] = useState<{ success: boolean; message: string } | null>(null);

  // Conversation inspection modal
  const [selectedConv, setSelectedConv] = useState<any | null>(null);

  const loadTelegramOverview = async () => {
    setLoading(true);
    try {
      console.info('[Admin Telegram] Fetching channel overview status...');
      const headers = await getAdminHeaders();
      const res = await fetch('/api/admin/telegram/overview', {
        headers,
      });
      if (!res.ok) {
        throw new Error(`Server returned HTTP ${res.status}`);
      }
      const json = await res.json();
      if (json.success) {
        setData(json);
        if (json.health && !diagnosticResult) {
          setDiagnosticResult(json.health);
        }
      }
    } catch (err: any) {
      console.error('[Admin Telegram] Failed to load Telegram overview:', err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTelegramOverview();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadTelegramOverview();
  };

  const handleRunDiagnostic = async () => {
    setDiagnosticRunning(true);
    setDiagnosticError(null);
    console.info('[Admin Telegram] Initiating server-side Telegram diagnostic...');
    try {
      const headers = await getAdminHeaders();
      const res = await fetch('/api/admin/telegram/diagnostic', {
        method: 'POST',
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.error || `Diagnostic request returned HTTP ${res.status}`);
      }
      console.info('[Admin Telegram] Diagnostic result received successfully:', json.overall);
      setDiagnosticResult(json);
      loadTelegramOverview();
    } catch (err: any) {
      console.error('[Admin Telegram Diagnostic Error]:', err.message);
      setDiagnosticError(err.message || 'Diagnostic failed to communicate with backend.');
    } finally {
      setDiagnosticRunning(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testChatId.trim()) return;
    setSendingTest(true);
    setTestResult(null);
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/telegram/test-message', {
        method: 'POST',
        headers,
        body: JSON.stringify({ chatId: testChatId.trim(), text: testMessageText }),
      });
      const json = await res.json();
      setTestResult(json);
      loadTelegramOverview();
    } catch (err: any) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setSendingTest(false);
    }
  };

  const handleRegisterWebhook = async () => {
    setRegisteringWebhook(true);
    setWebhookActionMsg(null);
    console.info('[Admin Telegram] Requesting server-side Webhook registration/repair...');
    try {
      const headers = await getAdminHeaders({ 'Content-Type': 'application/json' });
      const res = await fetch('/api/admin/telegram/webhook-setup', {
        method: 'POST',
        headers,
        body: JSON.stringify({ webhookUrl: webhookUrlInput.trim() || undefined }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || `Server returned HTTP ${res.status}`);
      }
      setWebhookActionMsg({
        success: true,
        message: json.message || 'Telegram webhook successfully registered with Telegram Bot API!',
      });
      if (json.diagnostic) {
        setDiagnosticResult(json.diagnostic);
      }
      loadTelegramOverview();
    } catch (err: any) {
      console.error('[Admin Telegram Webhook Error]:', err.message);
      setWebhookActionMsg({ success: false, message: err.message || 'Webhook registration failed.' });
    } finally {
      setRegisteringWebhook(false);
    }
  };

  const handleRemoveWebhook = async () => {
    if (!window.confirm('Are you sure you want to remove the Telegram webhook? Bot updates will stop arriving until re-registered.')) {
      return;
    }
    setRegisteringWebhook(true);
    setWebhookActionMsg(null);
    try {
      const headers = await getAdminHeaders();
      const res = await fetch('/api/admin/telegram/webhook-remove', {
        method: 'POST',
        headers,
      });
      const json = await res.json();
      setWebhookActionMsg({
        success: json.success,
        message: json.message || 'Webhook removed successfully.',
      });
      loadTelegramOverview();
    } catch (err: any) {
      setWebhookActionMsg({ success: false, message: err.message });
    } finally {
      setRegisteringWebhook(false);
    }
  };

  const handleClearErrors = async () => {
    try {
      const headers = await getAdminHeaders();
      await fetch('/api/admin/telegram/errors/clear', {
        method: 'POST',
        headers,
      });
      loadTelegramOverview();
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBadge = (status: string) => {
    const s = String(status || '').toUpperCase();
    if (['ONLINE', 'PASS', 'PROCESSED', 'SENT', 'HEALTHY', 'READY'].includes(s)) {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          {s === 'READY' ? 'READY' : s === 'PASS' ? 'PASS' : s === 'HEALTHY' ? 'HEALTHY' : 'ONLINE'}
        </span>
      );
    }
    if (['OFFLINE', 'NOT CONFIGURED', 'WARNING', 'DEGRADED', 'CONNECTING'].includes(s)) {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-500/20 text-amber-400 border border-amber-500/30">
          {s}
        </span>
      );
    }
    return (
      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-500/20 text-rose-400 border border-rose-500/30">
        {s === 'NOT READY' ? 'NOT READY' : s === 'FAIL' ? 'FAIL' : s === 'FAILED' ? 'FAILED' : s || 'ERROR'}
      </span>
    );
  };

  const productionWebhookUrl =
    data?.health?.details?.expectedWebhook?.url ||
    `${window.location.origin}/api/telegram/webhook`;

  return (
    <div className="space-y-8 pb-12">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 bg-blue-500/10 text-blue-400 rounded-2xl border border-blue-500/20">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-extrabold text-white">Operations & Desks: Telegram Operations</h1>
              <p className="text-xs text-slate-400 mt-0.5">
                Monitor, diagnose, and manage the Telegram AI Assistant channel, live webhooks, message pipelines, and customer conversations.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold flex items-center gap-2 border border-slate-700 transition-all shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-emerald-400' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh Status'}</span>
          </button>
        </div>
      </div>

      {/* 2. SYSTEM STATUS CARDS */}
      <div>
        <h2 className="text-sm font-extrabold text-slate-300 uppercase tracking-wider mb-4">
          Core System Status Cards
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                <Bot className="w-4 h-4 text-blue-400" /> Telegram Bot
              </span>
              {getStatusBadge(data?.config?.configured ? 'ONLINE' : 'NOT CONFIGURED')}
            </div>
            <div>
              <div className="text-sm font-bold text-white">
                {data?.config?.botUsername ? `@${data.config.botUsername}` : 'Not Configured'}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {data?.config?.botName || 'Bot name unavailable'}
              </div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                <Webhook className="w-4 h-4 text-purple-400" /> Telegram Webhook
              </span>
              {getStatusBadge((data?.webhookInfo?.configured && data?.webhookInfo?.info?.url) ? 'ONLINE' : 'OFFLINE — No Webhook Set')}
            </div>
            <div>
              <div className="text-sm font-bold text-white truncate">
                {data?.webhookInfo?.info?.url || 'No Webhook Set'}
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                Pending updates: {data?.webhookInfo?.info?.pendingUpdateCount || 0}
              </div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                <Server className="w-4 h-4 text-emerald-400" /> Render Backend
              </span>
              {getStatusBadge('ONLINE')}
            </div>
            <div>
              <div className="text-sm font-bold text-white">Active (Node.js)</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Express Server / Port 3000</div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-amber-400" /> Gemini AI
              </span>
              {getStatusBadge(
                (data?.health?.details?.gemini?.connected ??
                 (data?.health?.report || []).find((r: any) => r.component === 'Gemini')?.status === 'PASS')
                  ? 'ONLINE'
                  : 'OFFLINE'
              )}
            </div>
            <div>
              <div className="text-sm font-bold text-white">Google GenAI SDK</div>
              <div className="text-[11px] text-slate-400 mt-0.5">
                {data?.health?.details?.gemini?.model ? `Model: ${data.health.details.gemini.model}` : 'Intent-Driven Analysis'}
              </div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                <Database className="w-4 h-4 text-cyan-400" /> Supabase Database
              </span>
              {getStatusBadge(
                (data?.health?.details?.supabase?.connected ??
                 (data?.health?.report || []).find((r: any) => r.component === 'Supabase')?.status === 'PASS')
                  ? 'ONLINE'
                  : 'OFFLINE'
              )}
            </div>
            <div>
              <div className="text-sm font-bold text-white">PostgreSQL Connected</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Profiles & Job Matches</div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                <Send className="w-4 h-4 text-indigo-400" /> Outbound Messaging
              </span>
              {getStatusBadge(data?.config?.configured ? 'ONLINE' : 'NOT CONFIGURED')}
            </div>
            <div>
              <div className="text-sm font-bold text-white">Telegram Bot API</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Direct Dispatch Ready</div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                <MessageSquare className="w-4 h-4 text-teal-400" /> Inbound Messaging
              </span>
              {getStatusBadge((data?.webhookInfo?.configured && data?.webhookInfo?.info?.url) ? 'ONLINE' : 'OFFLINE')}
            </div>
            <div>
              <div className="text-sm font-bold text-white">Webhook Pipeline</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Real-time Update Ingestion</div>
            </div>
          </div>
        </div>
      </div>

      {/* OPERATIONS & DIAGNOSTIC CARD (TWO PROMINENT OPERATIONS BUTTONS) */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-base font-extrabold text-white flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" /> Telegram Operations & Desks Diagnostic
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Execute complete server-side verification and register or repair the real-time Telegram webhook.
            </p>
          </div>

          {/* TWO REQUIRED OPERATIONS BUTTONS */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRunDiagnostic}
              disabled={diagnosticRunning}
              className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold rounded-xl text-xs transition-all shadow-lg flex items-center gap-2 disabled:opacity-50 cursor-pointer"
            >
              <RefreshCw className={`w-4 h-4 ${diagnosticRunning ? 'animate-spin' : ''}`} />
              <span>{diagnosticRunning ? 'Running diagnostic...' : 'Run Telegram Diagnostic'}</span>
            </button>

            <button
              onClick={handleRegisterWebhook}
              disabled={registeringWebhook}
              className="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-extrabold rounded-xl text-xs transition-all shadow-lg flex items-center gap-2 disabled:opacity-50 cursor-pointer"
            >
              <Webhook className={`w-4 h-4 ${registeringWebhook ? 'animate-spin' : ''}`} />
              <span>{registeringWebhook ? 'Registering webhook...' : 'Register / Repair Telegram Webhook'}</span>
            </button>
          </div>
        </div>

        {/* Diagnostic Request Error Alert */}
        {diagnosticError && (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-4 flex items-start gap-3 text-rose-300 text-xs animate-fade-in">
            <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <div className="font-bold text-rose-200">Diagnostic Request Error</div>
              <div>{diagnosticError}</div>
              <div className="text-[11px] text-rose-400">Click &quot;Run Telegram Diagnostic&quot; to re-test server status.</div>
            </div>
          </div>
        )}

        {/* Webhook Action Banner */}
        {webhookActionMsg && (
          <div
            className={`p-4 rounded-2xl text-xs border flex items-center gap-3 animate-fade-in ${
              webhookActionMsg.success
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
            }`}
          >
            {webhookActionMsg.success ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
            )}
            <span className="font-semibold">{webhookActionMsg.message}</span>
          </div>
        )}

        {/* STRUCTURED DIAGNOSTIC DISPLAY */}
        {diagnosticResult && (
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-800/80 pb-4">
              <div>
                <h4 className="text-sm font-extrabold text-white">
                  Telegram Diagnostic
                </h4>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  Evaluated at: {new Date(diagnosticResult.timestamp || Date.now()).toLocaleTimeString()} UTC
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-300">Overall:</span>
                {getStatusBadge(
                  diagnosticResult.overall ||
                  (diagnosticResult.summary === 'HEALTHY' ? 'READY' : 'NOT READY')
                )}
              </div>
            </div>

            {/* 10 Diagnostic Items List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(
                diagnosticResult.items ||
                (diagnosticResult.report || []).map((r: any) => ({
                  name: r.component,
                  status: r.status,
                  message: r.message,
                }))
              ).map((item: any, idx: number) => {
                const isPass = item.status === 'PASS';
                return (
                  <div
                    key={idx}
                    className={`p-3.5 rounded-xl border flex flex-col justify-between gap-2 transition-all ${
                      isPass
                        ? 'bg-slate-900/90 border-slate-800/80 hover:border-emerald-500/30'
                        : 'bg-rose-950/20 border-rose-500/30'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-xs text-white tracking-wide">{item.name}</span>
                      {getStatusBadge(item.status)}
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed">
                      {item.message}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Overall Verdict Banner */}
            <div
              className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                (diagnosticResult.overall === 'READY' || diagnosticResult.summary === 'HEALTHY')
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                  : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
              }`}
            >
              <div className="flex items-center gap-2.5">
                {(diagnosticResult.overall === 'READY' || diagnosticResult.summary === 'HEALTHY') ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0" />
                )}
                <span className="text-xs font-bold">
                  {(diagnosticResult.overall === 'READY' || diagnosticResult.summary === 'HEALTHY')
                    ? 'All Telegram bot systems, Webhooks, Gemini AI, Supabase, and messaging pipelines are verified and READY.'
                    : 'One or more diagnostic checks failed. Review details above or click "Register / Repair Telegram Webhook".'}
                </span>
              </div>
              <span className="text-xs font-mono font-extrabold uppercase px-3 py-1 rounded-lg bg-slate-950/80 border border-slate-800 flex-shrink-0 text-center">
                Overall: {diagnosticResult.overall || (diagnosticResult.summary === 'HEALTHY' ? 'READY' : 'NOT READY')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 8. SEND TEST TELEGRAM MESSAGE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Send Test Telegram Message */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-5">
          <div>
            <h3 className="text-base font-extrabold text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-400" /> Send Test Telegram Message
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Dispatches a live test message directly to any Telegram chat ID via Bot API.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1">Telegram Chat ID</label>
              <input
                type="text"
                value={testChatId}
                onChange={(e) => setTestChatId(e.target.value)}
                placeholder="e.g. 123456789"
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1">Message Content</label>
              <input
                type="text"
                value={testMessageText}
                onChange={(e) => setTestMessageText(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <button
              onClick={handleSendTestMessage}
              disabled={sendingTest || !testChatId.trim()}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-extrabold rounded-xl text-xs transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Send className={`w-4 h-4 ${sendingTest ? 'animate-bounce' : ''}`} />
              <span>{sendingTest ? 'Sending Test Message...' : 'Send Test Telegram Message'}</span>
            </button>

            {testResult && (
              <div className={`p-3 rounded-xl text-xs font-mono border ${testResult.success ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-rose-500/10 border-rose-500/30 text-rose-300'}`}>
                {JSON.stringify(testResult, null, 2)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 12. & 13. WEBHOOK MANAGEMENT */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-5">
        <div>
          <h3 className="text-base font-extrabold text-white flex items-center gap-2">
            <Webhook className="w-5 h-5 text-purple-400" /> Webhook Registration & Management
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Register or refresh the production HTTPS webhook url with Telegram Bot API.
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400 font-semibold">Production Webhook URL:</span>
              <span className="font-mono text-emerald-400">{productionWebhookUrl}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400 font-semibold">Current Telegram Webhook:</span>
              <span className="font-mono text-slate-200">{data?.webhookInfo?.info?.url || 'NOT REGISTERED'}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Custom Webhook URL Override (Optional)</label>
            <input
              type="text"
              value={webhookUrlInput}
              onChange={(e) => setWebhookUrlInput(e.target.value)}
              placeholder={productionWebhookUrl}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-purple-500 font-mono"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRegisterWebhook}
              disabled={registeringWebhook}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-extrabold rounded-xl text-xs transition-all shadow-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${registeringWebhook ? 'animate-spin' : ''}`} />
              <span>Register / Refresh Telegram Webhook</span>
            </button>

            <button
              onClick={handleRemoveWebhook}
              disabled={registeringWebhook}
              className="px-5 py-2.5 bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 font-extrabold rounded-xl text-xs transition-all border border-rose-500/30 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Remove Telegram Webhook</span>
            </button>
          </div>

          {webhookActionMsg && (
            <div className={`p-3 rounded-xl text-xs font-bold border ${webhookActionMsg.success ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-rose-500/10 border-rose-500/30 text-rose-300'}`}>
              {webhookActionMsg.message}
            </div>
          )}
        </div>
      </div>

      {/* 10. LIVE OPERATIONS MONITOR */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-extrabold text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-400" /> Live Operations Monitor
          </h3>
          <span className="text-[10px] font-mono text-slate-400">Real-time event feed</span>
        </div>

        <div className="bg-slate-950 rounded-2xl border border-slate-800 p-4 max-h-72 overflow-y-auto space-y-2 font-mono text-xs">
          {data?.liveEvents && data.liveEvents.length > 0 ? (
            data.liveEvents.map((ev: any, idx: number) => (
              <div key={idx} className="flex items-center justify-between gap-4 py-1.5 border-b border-slate-900 last:border-0">
                <div className="flex items-center gap-3 truncate">
                  <span className="text-slate-500 text-[10px]">{new Date(ev.timestamp).toLocaleTimeString()}</span>
                  <span className="text-blue-400 font-bold">[{ev.id}]</span>
                  <span className="text-amber-400 font-semibold">{ev.eventType}</span>
                  <span className="text-slate-300 truncate">{ev.contentSnippet || ev.error || 'Event processed'}</span>
                </div>
                {getStatusBadge(ev.status)}
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-slate-500 text-xs">No live events recorded yet. Send a message to the Telegram bot to test.</div>
          )}
        </div>
      </div>

      {/* 14. TELEGRAM CONVERSATION DESK */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-extrabold text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-teal-400" /> Telegram Conversations Desk
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Active customer chat sessions linked to job requests.</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 font-extrabold text-[10px] uppercase">
                <th className="py-3 px-4">Customer / Username</th>
                <th className="py-3 px-4">Chat ID</th>
                <th className="py-3 px-4">State</th>
                <th className="py-3 px-4">Last Message</th>
                <th className="py-3 px-4">Last Activity</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {data?.conversations && data.conversations.length > 0 ? (
                data.conversations.map((conv: any) => (
                  <tr key={conv.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-3 px-4 font-bold text-white">
                      {conv.first_name || 'Customer'} {conv.username ? `(@${conv.username})` : ''}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-300">{conv.chat_id}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-300">
                        {conv.state}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-300 truncate max-w-xs">
                      {conv.draft_job_data?.problem || 'Active conversation'}
                    </td>
                    <td className="py-3 px-4 text-slate-400 font-mono text-[11px]">
                      {new Date(conv.last_message_at || conv.updated_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => setSelectedConv(conv)}
                        className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-bold transition-all flex items-center gap-1 ml-auto"
                      >
                        <Eye className="w-3.5 h-3.5" /> Inspect
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500 text-xs">
                    No active Telegram conversations found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 11. ERROR CENTER */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            <h3 className="text-base font-extrabold text-white">Telegram Error Center</h3>
          </div>
          <button
            onClick={handleClearErrors}
            className="px-3 py-1.5 bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 rounded-xl text-xs font-bold border border-rose-500/30 transition-all flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Clear resolved errors
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 font-extrabold text-[10px] uppercase">
                <th className="py-3 px-4">Time</th>
                <th className="py-3 px-4">Component</th>
                <th className="py-3 px-4">Error Type</th>
                <th className="py-3 px-4">Request ID</th>
                <th className="py-3 px-4">Message</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 font-mono text-[11px]">
              {data?.errorCenter && data.errorCenter.length > 0 ? (
                data.errorCenter.map((errItem: any) => (
                  <tr key={errItem.id} className="hover:bg-slate-800/40">
                    <td className="py-3 px-4 text-slate-400">{new Date(errItem.timestamp).toLocaleTimeString()}</td>
                    <td className="py-3 px-4 font-bold text-amber-400">{errItem.component}</td>
                    <td className="py-3 px-4 text-rose-400">{errItem.errorType}</td>
                    <td className="py-3 px-4 text-blue-400">{errItem.requestId}</td>
                    <td className="py-3 px-4 text-slate-300">{errItem.message}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-500 text-xs">
                    No Telegram errors recorded. System healthy.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Conversation Inspection Modal */}
      {selectedConv && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-2xl w-full p-6 shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h4 className="text-base font-extrabold text-white">Conversation Inspection</h4>
                <p className="text-xs text-slate-400">Chat ID: {selectedConv.chat_id} | Username: @{selectedConv.username || 'none'}</p>
              </div>
              <button
                onClick={() => setSelectedConv(null)}
                className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full text-xs"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2 text-xs">
                <div className="font-bold text-slate-300">Draft Job Data:</div>
                <pre className="text-emerald-400 font-mono text-[11px] overflow-x-auto">
                  {JSON.stringify(selectedConv.draft_job_data, null, 2)}
                </pre>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setSelectedConv(null)}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl text-xs"
              >
                Close Inspection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
